import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import { ValidationReviewInventoryTableFields } from '../HbtPortfolioManagementPage/types';
import {
  arrayFirstElementSortingFn,
  insuredLenderNameSortingFn,
  momentDateSortingFn,
  priorityNumberSortingFn
} from './sorting';
import {
  renderApplicationIdCell,
  renderPortfolioIdsCell,
  renderReceiptDateCell,
  renderInsuredLenderCodeCell,
  renderNumberOfLoansCell,
  createInsuredLenderNameRenderer,
  createOccupancyTypeRenderer,
  createTermInsuranceRenderer,
  createRequestTypeRenderer,
  createAmountSubmittedRenderer
} from './vriCellRenderer';
import { TableCellProps } from '@mui/material';

export const renderDataTestId = (columnId: string) => {
  return ({}: any) =>
    ({
      'data-testid': `column-${columnId}`
    }) as TableCellProps & Record<'data-testid', string>;
};

export const createPortfolioColumns = (
  t: TFunction,
  language: string
): MRT_ColumnDef<ValidationReviewInventoryTableFields>[] => {
  const insuredLenderNameRenderer = createInsuredLenderNameRenderer(language);
  const occupancyTypeRenderer = createOccupancyTypeRenderer(t);
  const termInsuranceRenderer = createTermInsuranceRenderer(t);
  const requestTypeRenderer = createRequestTypeRenderer(t);
  const amountSubmittedRenderer = createAmountSubmittedRenderer(language);
  const portfolioIdsCellRenderer = ({ cell }: { cell: any }) => renderPortfolioIdsCell({ cell }, t);

  return [
    {
      accessorKey: 'applicationId' as const,
      header: t('Pi-Portfolio-Management-Table-Column-RegistrationID'),
      Cell: renderApplicationIdCell,
      enableSorting: true,
      size: language === 'en' ? 190 : 200,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('applicationId')
    },
    {
      accessorKey: 'portfolioIds' as const,
      header: t('Pi-Portfolio-Management-Table-Column-PortfolioIDS'),
      Cell: portfolioIdsCellRenderer,
      enableSorting: true,
      size: language === 'en' ? 350 : 420,
      sortingFn: arrayFirstElementSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioIds')
    },
    {
      accessorKey: 'applicationRequestReceiptDate' as const,
      header: t('Pi-Portfolio-Management-Table-Column-RequestReceiptDate'),
      Cell: renderReceiptDateCell,
      enableSorting: true,
      size: language === 'en' ? 240 : 320,
      sortingFn: momentDateSortingFn,
      muiTableHeadCellProps: renderDataTestId('applicationRequestReceiptDate')
    },
    {
      accessorKey: 'insuredApprovedLenderCode' as const,
      header: t('Pi-Portfolio-Management-Table-Column-InsuredApprovedLenderCode'),
      Cell: renderInsuredLenderCodeCell,
      enableSorting: true,
      size: language === 'en' ? 310 : 300,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('insuredApprovedLenderCode')
    },
    {
      accessorKey: 'insuredApprovedLenderName' as const,
      header: t('Pi-Portfolio-Management-Table-Column-InsuredApprovedLenderName'),
      Cell: insuredLenderNameRenderer,
      enableSorting: true,
      size: language === 'en' ? 320 : 300,
      sortingFn: insuredLenderNameSortingFn,
      muiTableHeadCellProps: renderDataTestId('insuredApprovedLenderName')
    },
    {
      accessorKey: 'portfolioPoolPrecisionTypeCode' as const,
      header: t('Pi-Portfolio-Management-Table-Column-RequestType'),
      Cell: requestTypeRenderer,
      enableSorting: true,
      size: language === 'en' ? 180 : 210,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioPoolPrecisionTypeCode')
    },
    {
      accessorKey: 'portfolioOccupancyType' as const,
      header: t('Pi-Portfolio-Management-Table-Column-OccupancyType'),
      Cell: occupancyTypeRenderer,
      enableSorting: true,
      size: language === 'en' ? 200 : 200,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioOccupancyType')
    },
    {
      accessorKey: 'portfolioTermInsurance' as const,
      header: t('Pi-Portfolio-Management-Table-Column-TermInsurance'),
      Cell: termInsuranceRenderer,
      enableSorting: true,
      size: language === 'en' ? 200 : 230,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioTermInsurance')
    },
    {
      accessorKey: 'portfolioNumberOfLoansSubmitted' as const,
      header: t('Pi-Portfolio-Management-Table-Column-NumberOfLoansSubmitted'),
      Cell: renderNumberOfLoansCell,
      enableSorting: true,
      size: language === 'en' ? 250 : 260,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioNumberOfLoansSubmitted')
    },
    {
      accessorKey: 'portfolioAmountSubmitted' as const,
      header: t('Pi-Portfolio-Management-Table-Column-AmountSubmitted'),
      Cell: amountSubmittedRenderer,
      enableSorting: true,
      size: 260,
      sortingFn: priorityNumberSortingFn,
      muiTableHeadCellProps: renderDataTestId('portfolioAmountSubmitted')
    }
  ];
};
